import { useState } from "react";
import { message } from "antd";
import { isPolygonInPolygon } from "../utils/geometryUtils";

export const useEntityManagement = (form, { entityName, api }) => {
  const [entities, setEntities] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [zoomToEntity, setZoomToEntity] = useState(null);
  const [parentEntity, setParentEntity] = useState(null);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const data = await api.getEntities();
      setEntities(data);
    } catch (error) {
      message.error(`Failed to fetch ${entityName}s`);
    } finally {
      setLoading(false);
    }
  };

  const handlePolygonComplete = (data) => {
    setCurrentEntity(data);
    if (!isPolygonInPolygon(data.coordinates, parentEntity.coordinates)) {
      message.warning(
        `Veuillez dessiner à l'intérieur de la région ${parentEntity.name} s'il vous plaît.`
      );
      return;
    }
    setIsModalVisible(true);
    setDrawingMode(false);
  };

  const startEditingEntity = (entity) => {
    setEditingEntity({
      ...entity,
      coordinates: [...entity.coordinates],
    });
    setCurrentEntity({
      coordinates: [...entity.coordinates],
    });
    setIsEditing(true);
    setDrawingMode(false);
  };

  const handlePolygonEdit = (newCoordinates) => {
    setCurrentEntity((prev) => ({
      ...prev,
      coordinates: [...newCoordinates],
    }));
    setEditingEntity((prev) => ({
      ...prev,
      coordinates: [...newCoordinates],
    }));
  };

  const handleViewEntity = (entity) => {
    setZoomToEntity(entity);
    setTimeout(() => setZoomToEntity(null), 1000);
  };

  const cancelEditing = () => {
    setEditingEntity(null);
    setCurrentEntity(null);
    setIsEditing(false);
    setDrawingMode(true);
    form.resetFields();
    fetchEntities();
  };

  const handleSave = async (values) => {
    if (!currentEntity) {
      message.warning(`Please draw a ${entityName} first`);
      return;
    }
    if (loading) return;
    const entityData = {
      ...values,
      name: values.name,
      description: values.description,
      coordinates: currentEntity.coordinates,
    };

    if (values["line_details"])
      entityData["line_details"] = values["line_details"];
    try {
      setLoading(true);
      if (editingEntity) {
        await api.updateEntity(editingEntity.id, entityData);
        message.success(`${entityName} updated successfully`);
      } else {
        await api.createEntity(entityData);
        message.success(`${entityName} created successfully`);
      }

      await fetchEntities();
      handleModalClose();
    } catch (error) {
      message.error(
        editingEntity
          ? `Failed to update ${entityName}`
          : `Failed to save ${entityName}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setCurrentEntity(null);
    setEditingEntity(null);
    setIsEditing(false);
    setDrawingMode(true);
    form.resetFields();
  };

  const deleteEntity = async (entityId) => {
    try {
      setLoading(true);
      await api.deleteEntity(entityId);
      message.success(`${entityName} deleted successfully`);
      await fetchEntities();
    } catch (error) {
      message.error(`Failed to delete ${entityName}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    entities,
    currentEntity,
    isModalVisible,
    loading,
    drawingMode,
    editingEntity,
    isEditing,
    zoomToEntity,
    fetchEntities,
    handlePolygonComplete,
    startEditingEntity,
    handlePolygonEdit,
    handleViewEntity,
    cancelEditing,
    handleSave,
    handleModalClose,
    deleteEntity,
    setDrawingMode,
    setIsModalVisible,
    setParentEntity,
  };
};
